import { useEffect, useRef, useState } from 'react';
import SafeIcon from './SafeIcon';
import MaterialPicker, { type PickedItem } from './MaterialPicker';
import { TaskDetails } from './IdeasPage';
import { amyMark } from '../brand/logos';
import { useApp, timeLabel } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import type { Message } from '../store/seed';
import { localizeDemoDatesInVariables } from './campaignDisplay';

const TASK_STATUS_ICON: Record<string, string> = {
  Ready: 'FiCheckCircle',
  Failed: 'FiAlertTriangle',
  Working: 'FiLoader',
  Cancelled: 'FiXCircle',
  Waiting: 'FiHelpCircle',
  'With agency': 'FiUsers',
};
const DEFAULT_TASK_STATUS_ICON = 'FiClock';
const FOCUS_DELAY_AFTER_PREFILL_MS = 50;

export default function ChatPanel({
  open,
  onClose,
  prefill,
  thread: requested,
  reviewPostId,
  onOpenPost,
}: {
  open: boolean;
  onClose: () => void;
  prefill: string;
  thread?: { id: string; openRequestNumber: number } | null;
  reviewPostId?: string;
  onOpenPost: (id: string) => void;
}) {
  const { translate, readOnlyProps, readOnly, language } = useApp();
  const { route, actor, role, canApprove, notify } = useUi();
  const { commands } = useDemo();
  const { conversations, posts, workspace, tasks } = useWorkspaceData(route.workspaceId);
  const [threadId, setThreadId] = useState(conversations[0]?.id);
  const [text, setText] = useState('');
  const [attached, setAttached] = useState<PickedItem[]>([]);
  const [picking, setPicking] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const thread = conversations.find((item) => item.id === threadId) ?? conversations[0];
  const reviewPost = posts.find((post) => post.id === reviewPostId);

  useEffect(() => {
    if (!conversations.some((item) => item.id === threadId)) setThreadId(conversations[0]?.id);
  }, [conversations, threadId]);
  const lastAppliedThreadRequestRef = useRef(0);
  useEffect(() => {
    const requestedConversationExists =
      !!requested && conversations.some((item) => item.id === requested.id);
    if (
      requested &&
      requested.openRequestNumber !== lastAppliedThreadRequestRef.current &&
      requestedConversationExists
    ) {
      lastAppliedThreadRequestRef.current = requested.openRequestNumber;
      setThreadId(requested.id);
    }
  }, [requested, conversations]);
  useEffect(() => {
    if (prefill) {
      setText(prefill);
      window.setTimeout(() => inputRef.current?.focus(), FOCUS_DELAY_AFTER_PREFILL_MS);
    }
  }, [prefill]);
  const latestOpenThreadRef = useRef(thread);
  useEffect(() => {
    latestOpenThreadRef.current = thread;
  });
  const openThreadId = thread?.id;
  useEffect(() => {
    const openedThread = latestOpenThreadRef.current;
    if (openedThread?.unread) commands.markConversationRead(openedThread.id);
  }, [openThreadId, commands]);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [thread?.messages.length, thread?.id]);

  const hasNothingToSend = !text.trim() && !attached.length;

  function send() {
    if (readOnly || hasNothingToSend || !thread) return;
    commands.sendMessage(
      thread.id,
      text.trim() || attached.map((item) => item.name).join(', '),
      actor,
      attached.length ? attached.map((item) => ({ name: item.file, src: item.src })) : undefined
    );
    setText('');
    setAttached([]);
  }

  const formatLongNovemberDate = (day: number) =>
    new Intl.DateTimeFormat(language, { weekday: 'long', day: 'numeric', month: 'long' }).format(
      new Date(2026, 10, day)
    );

  function postCard(postId: string) {
    const post = posts.find((item) => item.id === postId);
    if (!post) return null;
    const onlyThePostUnderReviewHasActions = !!reviewPost;
    if (onlyThePostUnderReviewHasActions) return null;
    const needsReview = post.status === 'Needs your review';
    return (
      <div className="card chat-post-card">
        <img src={post.image} className="post-thumb" alt="" />
        <p style={{ margin: '8px 0 2px' }}>
          <strong>{post.title}</strong>
        </p>
        <p className="muted" style={{ margin: '0 0 8px', fontSize: 12 }}>
          {post.channel} · {formatLongNovemberDate(post.day)}, {post.time}
        </p>
        <div className="button-row">
          {needsReview &&
            role !== 'agency' &&
            (canApprove ? (
              <button
                className="button small primary"
                onClick={() => {
                  if (commands.approve(post.id, actor))
                    notify(translate('Approved. “{title}” is scheduled.', { title: post.title }));
                }}
                aria-label={translate('Approve & schedule: {post}', {
                  post: `${post.title} · ${post.channel}`,
                })}
                {...readOnlyProps}
              >
                {translate('Approve & schedule')}
              </button>
            ) : (
              <span className="status neutral">
                <SafeIcon name="FiClock" />
                {translate('Awaiting approval from {name}', { name: workspace.owner })}
              </span>
            ))}
          {!needsReview && <span className="status neutral">{translate(post.status)}</span>}
          <button className="button small" onClick={() => onOpenPost(post.id)}>
            {translate('Open post')}
          </button>
        </div>
      </div>
    );
  }

  function taskCard(message: Message) {
    const live = tasks.find((task) => task.id === message.taskId);
    const messageTask = message.task!;
    const status = live?.status ?? messageTask.status;
    const liveTitle = () =>
      live!.showAsWritten ? live!.title : translate(live!.title, live!.variables);
    const messageTaskTitle = () =>
      messageTask.showAsWritten ? messageTask.title : translate(messageTask.title);
    const title = live ? liveTitle() : messageTaskTitle();
    const icon = TASK_STATUS_ICON[status] ?? DEFAULT_TASK_STATUS_ICON;
    const plannedForLabel = live?.plannedFor
      ? ` · ${translate('planned for {day}', localizeDemoDatesInVariables({ day: `${live.plannedFor} Nov` }, language))}`
      : '';
    const canStartTask =
      live && (live.status === 'Failed' || (live.status === 'Queued' && live.plannedFor));
    return (
      <div className={`card chat-task task-${status.toLowerCase()}`}>
        <SafeIcon name={icon as 'FiClock'} size={14} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong>{title}</strong>
          <div className="muted">
            {translate('Task · {status}', { status: translate(status) })}
            {plannedForLabel} · {translate('demo, no real AI yet')}
          </div>
          {live && <TaskDetails task={live} compact />}
          {live && (
            <div className="button-row" style={{ marginTop: 6 }}>
              {live.result?.postIds?.slice(0, 1).map((id) => (
                <button key={id} className="button small" onClick={() => onOpenPost(id)}>
                  {translate('Open draft')}
                </button>
              ))}
              {canStartTask && (
                <button
                  className="button small"
                  onClick={() => commands.startTask(live.id)}
                  {...readOnlyProps}
                >
                  {translate(live.status === 'Failed' ? 'Retry' : 'Start now')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  function messageView(message: Message) {
    const displayedText = message.showAsWritten
      ? message.text
      : translate(message.text, localizeDemoDatesInVariables(message.variables, language));
    if (message.from === 'system')
      return (
        <div className="message system" key={message.id}>
          <span className="message-time">{message.at}</span>
          <div className="message-bubble">{displayedText}</div>
          {message.postId && !reviewPost && (
            <button className="link-button" onClick={() => onOpenPost(message.postId!)}>
              {translate('Open post')}
            </button>
          )}
        </div>
      );
    return (
      <div className={`message ${message.from === 'user' ? 'mine' : ''}`} key={message.id}>
        <span className="message-time">
          {message.from === 'amy' ? 'AMY' : message.by} ·{' '}
          {timeLabel(message.at, language, translate)}
        </span>
        <div className="message-bubble">{displayedText}</div>
        {message.attachments?.length ? (
          <div className="chat-attachments">
            {message.attachments.map((item) => (
              <span key={item.name} className="attachment-chip">
                {item.src ? (
                  <img src={item.src} alt="" />
                ) : (
                  <SafeIcon name="FiPaperclip" size={12} />
                )}
                {item.name}
              </span>
            ))}
          </div>
        ) : null}
        {message.task && message.from === 'amy' && !message.postId && taskCard(message)}
        {message.postId && postCard(message.postId)}
      </div>
    );
  }

  return (
    <aside className={`chat-panel ${open ? 'mobile-open' : ''}`} aria-label="AMY">
      <div className="chat-header">
        <button
          className="icon-button chat-mobile-back"
          onClick={onClose}
          aria-label={translate('Back')}
        >
          <SafeIcon name="FiArrowLeft" />
        </button>
        <div className="chat-title">
          <img className="amy-mark" src={amyMark} alt="" width={34} height={34} />
          <div>
            AMY{' '}
            <div className="muted" style={{ font: '12px var(--amy-font-ui)' }}>
              {workspace.name}
            </div>
          </div>
        </div>
        <button
          className="icon-button chat-close-button"
          onClick={onClose}
          aria-label={translate('Close chat')}
        >
          <SafeIcon name="FiX" />
        </button>
      </div>
      <div className="chat-thread-picker">
        <label htmlFor="chat-thread" className="muted">
          {translate('Conversation')}
        </label>
        <select
          id="chat-thread"
          className="select-field"
          value={thread?.id}
          onChange={(event) => setThreadId(event.target.value)}
        >
          {conversations.map((item) => {
            const conversationTitle = item.showAsWritten ? item.title : translate(item.title);
            const hasUnreadInOtherThread = item.unread && item.id !== thread?.id;
            return (
              <option key={item.id} value={item.id}>
                {hasUnreadInOtherThread
                  ? translate('{name} · {n} new', { name: conversationTitle, n: item.unread })
                  : conversationTitle}
              </option>
            );
          })}
        </select>
      </div>
      {reviewPost && (
        <div className="chat-review-context">
          <SafeIcon name="FiEye" size={14} />
          <span>
            {translate('Reviewing: {post}', {
              post: `${reviewPost.title} · ${reviewPost.channel}`,
            })}
          </span>
        </div>
      )}
      <div className="chat-messages" ref={listRef}>
        {thread?.messages.map(messageView)}
        {reviewPost && (
          <div className="notice-banner chat-review-note">
            <SafeIcon name="FiInfo" />
            <span>
              {translate(
                'Use the buttons under the post to approve or request changes. Ask AMY here if something is unclear.'
              )}
            </span>
          </div>
        )}
      </div>
      <div className="chat-compose">
        <div className="compose-box">
          {attached.length > 0 && (
            <div className="chat-attachments compose">
              {attached.map((item) => (
                <span key={item.source + item.file} className="attachment-chip">
                  {item.src ? (
                    <img src={item.src} alt="" />
                  ) : (
                    <SafeIcon name="FiPaperclip" size={12} />
                  )}
                  {item.file}
                  <button
                    aria-label={translate('Remove {word}', { word: item.file })}
                    onClick={() =>
                      setAttached(attached.filter((attachment) => attachment !== item))
                    }
                  >
                    <SafeIcon name="FiX" size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <textarea
            ref={inputRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
            placeholder={translate('Ask AMY anything…')}
            aria-label={translate('Ask AMY anything…')}
            {...readOnlyProps}
          />
          <div className="compose-actions">
            <button
              className="icon-button"
              onClick={() => setPicking(true)}
              aria-label={translate('Add attachment')}
              {...readOnlyProps}
            >
              <SafeIcon name="FiPaperclip" />
            </button>
            <button
              className="button primary small"
              onClick={send}
              disabled={readOnly || hasNothingToSend}
            >
              <SafeIcon name="FiSend" size={14} />
              {translate('Send')}
            </button>
          </div>
        </div>
      </div>
      {picking && (
        <MaterialPicker
          title="Attach photos, videos or documents"
          multiple
          onCancel={() => setPicking(false)}
          onPick={(items) => {
            setAttached([
              ...attached,
              ...items.filter(
                (item) => !attached.some((attachment) => attachment.file === item.file)
              ),
            ]);
            setPicking(false);
          }}
        />
      )}
    </aside>
  );
}
